import React, { useState, useEffect } from 'react';
import { Bell, Loader2, Save, Smartphone } from 'lucide-react';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import { NotificationPreferences } from '../../types';

const NotificationsView = () => {
    const { user, profile } = useAuth();
    const isVendedor = profile?.role === 'vendedor' || profile?.role === 'vendedor_parceiro';
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);

    useEffect(() => {
        if (user) {
            loadPrefs();
        }
    }, [user]);

    const loadPrefs = async () => {
        try {
            const { data, error } = await supabase
                .from('notification_preferences')
                .select('*')
                .eq('user_id', user?.id)
                .single();

            if (error && error.code === 'PGRST116') {
                // If not found, try to create default
                const { data: newData, error: createError } = await supabase
                    .from('notification_preferences')
                    .insert({ user_id: user?.id })
                    .select()
                    .single();

                if (createError) throw createError;
                setPrefs(newData as NotificationPreferences);
                return;
            } else if (error) {
                throw error;
            }

            if (data) {
                setPrefs(data as NotificationPreferences);
            }
        } catch (error) {
            console.error('Error loading preferences:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (key: keyof NotificationPreferences) => {
        if (!prefs) return;
        setPrefs({ ...prefs, [key]: !prefs[key] });
    };

    const handleSave = async () => {
        if (!prefs || !user) return;
        setSaving(true);
        try {
            await supabase
                .from('notification_preferences')
                .upsert(prefs);
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'notificacoes', acao: 'salvar_preferencias_notificacao', origem: 'acao' });
            alert('Preferências salvas com sucesso!');
        } catch (error) {
            console.error('Error saving:', error);
            alert('Erro ao salvar preferências.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <LoadingSpinner label="Carregando preferências..." />;

    if (!prefs) return (
        <div className="p-8 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400 rounded-xl">
            Não foi possível carregar as preferências. Tente recarregar a página.
        </div>
    );

    return (
        <div className="w-full animate-fade-in space-y-8">
            <div>
                <h2 className="text-xl font-bold font-poppins text-gray-900 dark:text-white">Notificações</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Escolha como e quando você quer ser notificado.</p>
            </div>

            {/* Platform Settings */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm space-y-6 transition-colors">
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shrink-0">
                        <Smartphone size={20} />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-gray-900 dark:text-white">Push Notifications</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Notificações exibidas dentro da plataforma (Web/App).</p>
                    </div>
                    <div className="ml-auto">
                        <Toggle
                            enabled={prefs.push_enabled}
                            onChange={() => handleToggle('push_enabled')}
                        />
                    </div>
                </div>

                {prefs.push_enabled && (
                    <div className="pl-14 space-y-6 animate-fade-in">
                        <div className="space-y-4">
                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Tarefas</h4>

                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Nova tarefa atribuída a mim</span>
                                <Toggle enabled={prefs.task_assigned} onChange={() => handleToggle('task_assigned')} />
                            </div>

                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Menções em tarefas</span>
                                <Toggle enabled={prefs.task_mention} onChange={() => handleToggle('task_mention')} />
                            </div>

                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tarefa concluída pelo responsável</span>
                                <Toggle enabled={prefs.task_completed} onChange={() => handleToggle('task_completed')} />
                            </div>
                        </div>

                        <div className="h-px bg-gray-100 dark:bg-gray-700"></div>

                        <div className="space-y-4">
                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Negociações (CRM)</h4>

                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Nova negociação atribuída a mim</span>
                                <Toggle enabled={prefs.negotiation_assigned} onChange={() => handleToggle('negotiation_assigned')} />
                            </div>

                            <div className="flex items-center justify-between py-2">
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Menções em negociações
                                    <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px] rounded">Em breve</span>
                                </div>
                                <Toggle enabled={prefs.negotiation_mention} onChange={() => handleToggle('negotiation_mention')} />
                            </div>

                            {isVendedor && (
                                <>
                                    <div className="flex items-center justify-between py-2">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Contrato assinado pelo cliente</span>
                                        <Toggle enabled={prefs.contract_signed} onChange={() => handleToggle('contract_signed')} />
                                    </div>

                                    <div className="flex items-center justify-between py-2">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Lembrete a cada 48h de contrato aguardando assinatura</span>
                                        <Toggle enabled={prefs.contract_pending_48h} onChange={() => handleToggle('contract_pending_48h')} />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex justify-end pt-4">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl font-bold hover:bg-primary/90 transition-all disabled:opacity-70 shadow-lg shadow-primary/20"
                >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    Salvar Preferências
                </button>
            </div>
        </div>
    );
};

// Helper Toggle Component
const Toggle = ({ enabled, onChange }: { enabled: boolean, onChange: () => void }) => (
    <button
        onClick={onChange}
        className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out ${enabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'}`}
    >
        <div
            className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${enabled ? 'translate-x-5' : 'translate-x-0'}`}
        />
    </button>
);

export default NotificationsView;
